import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Fuse from "fuse.js";
import {
  CheckCircle as CheckCircleIcon,
  PersonOff as PersonOffIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  BrokenImage as BrokenImageIcon,
  HelpOutline as HelpOutlineIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { RootState } from "../../../redux/store";
import { fetchUsers } from "../../../redux/actions/userActions";
import type {
  FormSectionOutput,
  FormQuestionOutput,
} from "../../../redux/types/formTypes";
import { hasAnswerContent } from "./assessmentHelpers";

interface AssessedSessionsTableProps {
  assessedSessions: any[];
  sessionsLoading: boolean;
  assessmentId: string;
  processingType?: "memory" | "form" | "roleplay";
  initialChatSessionFilter?: string;
}

const AssessedSessionsTable: React.FC<AssessedSessionsTableProps> = ({
  assessedSessions,
  sessionsLoading,
  assessmentId,
  processingType,
  initialChatSessionFilter,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState(
    initialChatSessionFilter || ""
  );
  const [hideSpam, setHideSpam] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Get users from Redux store
  const users = useSelector((state: RootState) => state.users.users);
  const usersLoading = useSelector((state: RootState) => state.users.loading);

  // Create a map of user_id to user email for quick lookup
  const userEmailMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    users.forEach((user) => {
      map[user.id] = user.email || "No email";
    });
    return map;
  }, [users]);

  // Fetch users when component mounts
  useEffect(() => {
    if (users.length === 0 && !usersLoading) {
      dispatch(fetchUsers() as any);
    }
  }, [dispatch, users.length, usersLoading]);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Process sessions to add composite ID and formatted date
  const processedSessions = useMemo(() => {
    return assessedSessions.map((session) => {
      const baseSession = {
        ...session,
        id: `${session.organization_id}:${session.conversation_id}:${
          session.chat_session_id
        }:${session.analysis_graph_info?.major_version || 1}`,
        createdDate: session._ts
          ? new Date(session._ts * 1000)
          : session.created_at
          ? new Date(session.created_at)
          : null,
        // Add spam label
        spamLabel: session.analysis_results?.assessed_spam?.label,
        // Add user email for search
        userEmail: userEmailMap[session.user_id] || "",
      };

      // Add processing-type-specific fields
      if (processingType === "form") {
        // Calculate fields filled for form processing type
        const formOutputs: FormSectionOutput[] =
          session.form_analysis?.form_outputs || [];
        let totalFields = 0;
        let filledFields = 0;

        formOutputs.forEach((section: FormSectionOutput) => {
          section.answers?.forEach((answer: FormQuestionOutput) => {
            totalFields++;
            if (hasAnswerContent(answer.llm_answer)) {
              filledFields++;
            }
          });
        });

        return {
          ...baseSession,
          fieldsFilled:
            totalFields > 0 ? `${filledFields}/${totalFields}` : "-",
        };
      } else {
        // Memory processing type - add score and recommendation
        return {
          ...baseSession,
          score: session.analysis_results?.summary?.overall_score,
          recommendation: session.analysis_results?.summary?.recommendation,
        };
      }
    });
  }, [assessedSessions, userEmailMap, processingType]);

  // Apply search filter and sort by creation time
  const filteredSessions = useMemo(() => {
    let sessions = processedSessions;

    // Filter out spam sessions if hideSpam is true
    if (hideSpam) {
      sessions = sessions.filter(
        (session) => !session.spamLabel || session.spamLabel === "None"
      );
    }

    if (searchQuery) {
      const fuse = new Fuse(sessions, {
        keys: [
          "chat_session_id",
          "user_id",
          "userEmail",
          "conversation_id",
          "source_assessment_id",
          "state",
          "analysis_results.summary.recommendation",
        ],
        threshold: 0.3,
      });
      sessions = fuse.search(searchQuery).map((result) => result.item);
    }

    // Create a copy of the array before sorting to avoid mutating read-only array
    return [...sessions].sort((a, b) => {
      if (!a.createdDate && !b.createdDate) return 0;
      if (!a.createdDate) return 1;
      if (!b.createdDate) return -1;
      return b.createdDate.getTime() - a.createdDate.getTime();
    });
  }, [searchQuery, processedSessions, hideSpam]);

  // Combine columns based on processing type
  const columns: GridColDef[] = useMemo(() => {
    // Define base columns that are shared across all processing types
    const baseColumns: GridColDef[] = [
      {
        field: "chat_session_id",
        headerName: "Chat Session ID",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={params.value as string}
            >
              {(params.value as string)?.substring(0, 8)}...
            </Typography>
          </Box>
        ),
      },
      {
        field: "id",
        headerName: "Assessment Session ID",
        flex: 1,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={0.5}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
              title={params.value as string}
            >
              {params.value}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyId(params.value as string)}
              sx={{ p: 0.25 }}
            >
              {copiedId === params.value ? (
                <CheckCircleIcon
                  fontSize="small"
                  color="success"
                  sx={{ fontSize: 14 }}
                />
              ) : (
                <ContentCopyIcon fontSize="small" sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        ),
      },
      {
        field: "source_assessment_id",
        headerName: "Assessment ID",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const isCurrentAssessment = params.value === assessmentId;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={0.5}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const orgId = params.row?.organization_id;
                  if (orgId && params.value) {
                    navigate(`/dashboard/assessments/${orgId}/${params.value}`);
                  }
                }}
                sx={{
                  fontFamily: "monospace",
                  textTransform: "none",
                  p: 0.25,
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={params.value as string}
              >
                {(params.value as string)?.substring(0, 8)}...
              </Button>
              {!isCurrentAssessment && (
                <Tooltip title="Different assessment version">
                  <Chip
                    label="V"
                    size="small"
                    color="info"
                    sx={{
                      height: 16,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": {
                        px: 0.5,
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "user_id",
        headerName: "User Email",
        width: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const userId = params.value;
          const userEmail = userEmailMap[userId];

          // If user not found in map and we're not loading, show "User not found"
          if (!userEmail && !usersLoading) {
            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  User not found
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={0.5}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/dashboard/users/${userId}`)}
                sx={{
                  textTransform: "none",
                  p: 0.25,
                  fontSize: "0.75rem",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={userEmail || "Loading..."}
              >
                {userEmail || "Loading..."}
              </Button>
            </Box>
          );
        },
      },
      {
        field: "conversation_id",
        headerName: "Conversation",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate(`/dashboard/conversations/${params.value}`)
              }
              sx={{
                fontFamily: "monospace",
                textTransform: "none",
                p: 0.25,
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={params.value as string}
            >
              {(params.value as string)?.substring(0, 8)}...
            </Button>
          </Box>
        ),
      },
    ];

    // Memory-specific columns
    const memoryColumns: GridColDef[] = [
      {
        field: "score",
        headerName: "Score",
        width: 70,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const score = params.value;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              {score !== undefined ? (
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {score}/5
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  -
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "recommendation",
        headerName: "Recommendation",
        width: 130,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const recommendation = params.value;
          const spamLabel = params.row?.spamLabel;

          // If there's a spam label (and it's not "None"), show that instead
          if (spamLabel && spamLabel !== "None") {
            let icon, color, title;

            switch (spamLabel) {
              case "NoEngagement":
                icon = <PersonOffIcon fontSize="small" />;
                color = "warning";
                title = "No Engagement";
                break;
              case "OffTopicConversation":
                icon = <ChatBubbleOutlineIcon fontSize="small" />;
                color = "info";
                title = "Off-Topic";
                break;
              case "IncoherentTranscript":
                icon = <BrokenImageIcon fontSize="small" />;
                color = "error";
                title = "Incoherent";
                break;
              case "Other":
                icon = <HelpOutlineIcon fontSize="small" />;
                color = "default";
                title = "Other";
                break;
              default:
                icon = <WarningIcon fontSize="small" />;
                color = "error";
                title = spamLabel;
            }

            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Tooltip title={`Spam: ${title}`}>
                  <Chip
                    icon={icon}
                    label={title}
                    size="small"
                    color={color as any}
                    variant="outlined"
                  />
                </Tooltip>
              </Box>
            );
          }

          // Otherwise show the recommendation
          const score = params.row?.score;

          // Handle both new enum values and old string values for backward compatibility
          let displayLabel = recommendation;

          if (recommendation) {
            // New enum values - show only emoji
            if (recommendation === "Thumbs up") {
              displayLabel = "👍";
            } else if (recommendation === "Thumbs down") {
              displayLabel = "👎";
            } else if (recommendation === "Mixed") {
              displayLabel = "🤔";
            }
            // Old string values (backward compatibility) - keep original format
            else {
              displayLabel = recommendation.split(":")[0].trim();
            }
          }

          // Always use score for color determination
          const chipColor =
            score >= 4 ? "success" : score < 2.75 ? "error" : "warning";

          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              {recommendation ? (
                <Chip
                  label={displayLabel}
                  size="small"
                  color={chipColor as any}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}
            </Box>
          );
        },
      },
    ];

    // Form-specific columns
    const formColumns: GridColDef[] = [
      {
        field: "fieldsFilled",
        headerName: "Fields Filled",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
    ];

    // Shared trailing columns (created date and actions)
    const trailingColumns: GridColDef[] = [
      {
        field: "createdDate",
        headerName: "Created",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {params.row.createdDate
                ? `${(params.row.createdDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${params.row.createdDate
                    .getDate()
                    .toString()
                    .padStart(
                      2,
                      "0"
                    )}, ${params.row.createdDate.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              size="small"
              onClick={() => {
                navigate(
                  `/dashboard/assessments/${assessmentId}/sessions/${params.row?.id}?org=${params.row?.organization_id}`
                );
              }}
              disabled={params.row?.state !== "ready"}
              sx={{ fontSize: "0.75rem", minWidth: "auto", px: 1 }}
            >
              View
            </Button>
          </Box>
        ),
      },
    ];

    // Return the combined columns based on processing type
    if (processingType === "form") {
      return [...baseColumns, ...formColumns, ...trailingColumns];
    } else {
      return [...baseColumns, ...memoryColumns, ...trailingColumns];
    }
  }, [
    processingType,
    assessmentId,
    navigate,
    copiedId,
    userEmailMap,
    usersLoading,
  ]);

  if (sessionsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (assessedSessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No sessions have been completed for this assessment yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <TextField
          label="Search Sessions"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by ID, email, state..."
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={hideSpam}
              onChange={(e) => setHideSpam(e.target.checked)}
              size="small"
            />
          }
          label="Hide spam sessions"
        />
      </Box>
      <Box
        sx={{ height: "calc(100vh - 550px)", minHeight: 400, width: "100%" }}
      >
        <DataGrid
          rows={filteredSessions}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
              py: 1,
            },
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.875rem",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AssessedSessionsTable;
